<template>
    <div :class="classBuilder('RPReportCard')">
        <div class="RPReportCard__status-container">
            <StreamlineIcon
                v-if="status === stat.SUCCESS"
                :icon="CheckCircle1"
                :fill="success"
                :size="16"
            />
            <StreamlineIcon
                v-if="status === stat.ERROR"
                :icon="RemoveCircleBold"
                stroke="transparent"
                :fill="error"
                :size="16"
            />
            <div
                v-if="status === stat.GENERATING"
                class="RPReportCard__generating"
            >
                <StreamlineIcon
                    class="RPReportCard__generating-icon"
                    :icon="SynchronizeArrows1"
                    :size="12"
                    :stroke="white"
                    :fill="white"
                />
            </div>
        </div>
        <div class="RPReportCard__content">
            <div class="RPReportCard__title">
                {{ reportTitle }}
                <span
                    v-if="showDate"
                    class="RPReportCard__date"
                >{{ date }}</span>
                <span class="RPReportCard__time">{{ time }}</span>
            </div>
            <div
                :class="[
                    'RPReportCard__message',
                    card && 'RPReportCard__message--card']
                "
            >
                <div class="RPReportCard__message-text">
                    {{ reportMessage }}
                </div>
                <div
                    v-if="showDownloadLink"
                    class="RPReportCard__download"
                    @click="$emit('download', {fileName, reportId: report.id})"
                >
                    <span class="RPReportCard__download-link">{{ card ? 'download here' : 'download' }}</span>
                    <span v-if="!card">!</span>
                    <span
                        v-if="card"
                        class="RPReportCard__download-icon"
                    >
                        <StreamlineIcon
                            :size="16"
                            :icon="CloudDownload"
                            :stroke="success400"
                        />
                    </span>
                </div>
            </div>
        </div>
        <div
            v-if="status === stat.SUCCESS"
            class="RPReportCard__delete"
            @click="deleteReport"
        >
            <StreamlineIcon
                :icon="Close"
                :stroke="textGrey"
                :size="14"
            />
        </div>
    </div>
</template>
<script>
import StreamlineIcon from "@reviewpush/streamline-icons-vue";
import {CheckCircle1} from "@reviewpush/streamline-bold/lib/interface-essential/FormValidation";
import {SynchronizeArrows1} from "@reviewpush/streamline-bold/lib/interface-essential/Synchronize";
import {CloudDownload} from "@reviewpush/streamline-regular/lib/internet-networks-servers/Cloud";
import {Close} from "@reviewpush/streamline-regular/lib/interface-essential/FormValidation";
import stat from 'status';
import statusBuilder from 'status/statusBuilder';
import has from 'lodash/has';
import moment from 'moment';
import {success, error, success400, textGrey, white} from 'style/_colors.scss';
import {RemoveCircleBold} from "@reviewpush/streamline-bold/lib/interface-essential/RemoveAdd";

const NotificationValidation = (val) => {
    return has(val, 'id')
      && has(val, 'title');
};

export default {
    name: 'RPReportCard',
    components: {
        StreamlineIcon
    },
    props: {
        read: {
            type: Boolean,
            default: false,
        },
        card: {
            type: Boolean,
            default: true,
        },
        report: {
            type: Object,
            required: true,
            validation: NotificationValidation,
        },
    },
    data(){
        return {
            stat,
            Close,
            SynchronizeArrows1,
            CheckCircle1,
            RemoveCircleBold,
            CloudDownload,
            success400,
            error,
            success,
            textGrey,
            white
        };
    },
    computed: {
        status() {
            switch(this.report.status) {
            case'Ready':
                return this.stat.SUCCESS;
            case'Pending':
            case'Generating':
                return this.stat.GENERATING;
            case'Failed':
                return this.stat.ERROR;
            default:
                return this.stat.STANDBY;
            }
        },
        fileFormat(){
            return this.report.format.toUpperCase();
        },
        reportTitle(){
            switch(this.status){
            case stat.GENERATING:
                return `Generating Report`;
            case stat.SUCCESS:
                return `Report Generated`;
            default:
                return `Report Failed`;
            }
        },
        reportMessage(){
            switch(this.status){
            case stat.GENERATING:
                return `Your ${this.fileFormat} is being generated and will be available for download shortly...`;
            case stat.SUCCESS:
                return this.card
                    ? `Your ${this.fileFormat} has successfully generated and is ready to `
                    : `Your ${this.fileFormat} file is ready to `;
            default:
                return `Your ${this.fileFormat} has failed to generate.`;
            }
        },
        dateTime(){
            return moment(this.report.created_at);
        },
        showDate(){
            return moment().diff(this.dateTime, 'days') >= 1;
        },
        date() {
            return (moment().diff(this.dateTime, 'days') === 1)
                ? 'Yesterday'
                : this.dateTime.format('MMM D');
        },
        time() {
            return moment(this.dateTime).format('h:mm A');
        },
        showDownloadLink(){
            return this.status === this.stat.SUCCESS;
        },
        fileName(){
            return this.showDownloadLink
                ? `${this.report.filename}.${this.report.format}`
                : '';
        }
    },
    methods: {
        classBuilder(className){
            return statusBuilder(
                className,
                this.status,
                false,
                {
                    'card': this.card
                }
            );
        },
        deleteReport(){
            this.$emit('delete', this.report.id);
        }
    },
};
</script>

<style lang="scss">
    @keyframes spin {
        100% {
            transform:rotate(-360deg);
        }
    }
    .RPReportCard {
        padding: 1rem;
        border-radius: 4px;
        display: flex;
        align-items: center;
        height: 3.5rem;
        width: 19rem;

        &--card {
            height: auto;
            width: 100%;
            border: 1px solid transparent;
            filter: drop-shadow(0px 2px 5px rgba(0, 0, 0, 0.08));
            background: $white;
        }

        &--generating {
            background: $primary-50;
            border-color: $primary;
        }
        &--success {
            background: $success-50;
            border-color: $success-green;
        }
        &--error {
            background: $error-50;
            border-color: $error-red;
        }

        &__status-container {
            margin-right: 1rem;
            display: flex;
            align-items: center;
        }
        &__generating {
            background: $primary;
            border-radius: 90%;
            height: 1rem;
            width: 1rem;
            display: flex;
            align-items: center;
            justify-content: space-around;
        }
        &__generating-icon {
            animation:spin 2s linear infinite;
        }
        &__content {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
        }
        &__title {
            font-weight: 600;
        }
        &__date,
        &__time {
            color: $text-grey;
            font-size: 0.75rem;
            font-weight: normal;
        }
        &__date {
            margin-right: 0.25rem;
        }
        &__message {
            display: flex;
            align-items: center;
            color: $text-grey;
            width: 14rem;

            &--card {
                width: auto
            }
        }
        &__message-text {
            white-space: nowrap;
            text-overflow: ellipsis;
            display: block;
            overflow: hidden;
        }
        &__download {
            cursor: pointer;
            display: flex;
            align-items: center;
            margin-left: 0.25rem;
            &:hover {
                .RPReportCard__download-link {
                    text-decoration: underline;
                }
            }

            &-link{
                font-weight: bold;
                color: $success-400;
            }
            &-icon{
                margin-left: 0.25rem;
            }
        }
        &__delete {
            cursor: pointer;
        }
    }
</style>

<docs>
    ```vue
    <RPReportCard :report="{
        'type': 'reports',
        'id': '61c04350-8244-4d29-9977-e5b5ef7519a3',
        'status': 'Failed',
        'filename': 'collective-report',
        'format': 'csv',
        'notify_email': 'company@reviewpush.com',
        'created_at': '2020-10-24 19:12:08'
    }"></RPReportCard>
    ```
    ```vue
    <RPReportCard :report="{
        'type': 'reports',
        'id': '270909a9-5b6b-4c5e-b17a-2d655d5aa559',
        'status': 'Ready',
        'filename': 'collective-report',
        'format': 'csv',
        'notify_email': 'company@reviewpush.com',
        'created_at': '2020-10-24 19:29:15'

    }"></RPReportCard>
    ```
    ```vue
    <RPReportCard :report="{
        'type': 'reports',
        'id': '3fa8517d-de10-43f0-ad86-3d1937870892',
        'status': 'Generating',
        'filename': 'collective-report',
        'format': 'csv',
        'notify_email': 'company@reviewpush.com',
        'created_at': '2020-10-24 21:06:18'
    }"></RPReportCard>
    ```
</docs>
